/**
 * Counter Module
 *
 * GSAP-powered number counter that triggers on scroll into viewport.
 * Supports decimals, prefixes, and suffixes with smooth easing.
 *
 * Usage:
 *   <span data-hs-anim="counter">1000</span>
 *
 * Features:
 *   - Auto-detects decimals, prefixes, suffixes
 *   - Respects prefers-reduced-motion
 *   - Triggers on scroll into viewport
 *   - Accessible with aria-live announcements
 */

import { getGsap, prefersReducedMotion, warnMissingDependencies, type GsapTimeline } from '@utils';

// Selectors
const SELECTORS = {
  counter: '[data-hs-anim="counter"]',
};

export async function init() {
  // Skip if user prefers reduced motion
  if (prefersReducedMotion()) {
    return { result: 'counter skipped - prefers-reduced-motion enabled' };
  }

  const gsapLib = getGsap('counter');
  if (!gsapLib) {
    return { result: 'counter skipped - GSAP not loaded' };
  }

  const { gsap, ScrollTrigger } = gsapLib;

  // ScrollTrigger is required for scroll-based counter animations
  if (!ScrollTrigger) {
    warnMissingDependencies('counter', ['ScrollTrigger'], 'error');
    return { result: 'counter skipped - ScrollTrigger not loaded' };
  }

  gsap.registerPlugin(ScrollTrigger);

  const counters = Array.from(document.querySelectorAll(SELECTORS.counter));
  const cleanup = {
    timelines: [] as GsapTimeline[],
    scrollTriggers: [] as any[],
  };

  function parseText(text: string) {
    // Remove commas from the text before parsing
    const cleanText = text.replace(/,/g, '');
    const match = cleanText.match(/([^\d.]*)(\d*\.?\d+)(.*)/);
    if (!match) {
      return { prefix: '', number: 0, suffix: '' };
    }
    return {
      prefix: match[1] || '',
      number: parseFloat(match[2]),
      suffix: match[3] || '',
    };
  }

  counters.forEach((counter) => {
    const htmlElement = counter as HTMLElement;
    const originalText = htmlElement.textContent?.trim() || '';
    const parsed = parseText(originalText);
    const decimals = (parsed.number.toString().split('.')[1] || '').length;

    // Check if original had commas (for numbers >= 1000)
    const hasCommas = originalText.includes(',');

    // Add accessibility attributes
    htmlElement.setAttribute('aria-live', 'polite');
    htmlElement.setAttribute('aria-atomic', 'true');

    // Create a temporary object to animate
    const counterObj = { value: 0 };

    // Create GSAP animation
    const tl = gsap.timeline({ paused: true });

    tl.to(counterObj, {
      value: parsed.number,
      duration: 3,
      ease: 'power4.out',
      onUpdate: function () {
        const currentValue = counterObj.value;
        let formatted =
          decimals > 0 ? currentValue.toFixed(decimals) : Math.round(currentValue).toString();

        // Add commas if original had them
        if (hasCommas) {
          formatted = formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        }

        htmlElement.textContent = `${parsed.prefix}${formatted}${parsed.suffix}`;
      },
      onComplete: function () {
        // Set final exact value
        htmlElement.textContent = originalText;
      },
    });

    cleanup.timelines.push(tl);

    // Use ScrollTrigger to play animation on scroll into view
    const st = ScrollTrigger.create({
      trigger: counter,
      start: 'top bottom',
      once: true,
      onEnter: () => {
        tl.play();
      },
    });
    cleanup.scrollTriggers.push(st);
  });

  return {
    result: `counter initialized (${counters.length} instances)`,
    destroy: () => {
      // Kill all timelines
      cleanup.timelines.forEach((tl) => {
        if (tl) tl.kill();
      });

      // Kill all scroll triggers
      cleanup.scrollTriggers.forEach((st) => {
        if (st) (st as any).kill();
      });

      // Clear arrays
      cleanup.timelines.length = 0;
      cleanup.scrollTriggers.length = 0;
    },
  };
}
